/**
 * /winning/search-creds — "Search Creds & Slide Creator". Migrated from
 * nuadvisory SlideCreator.cshtml + slideCreatorPage.js.
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns
 * ONLY its page content — no shell wrapper (double-shell otherwise).
 *
 * Frontend-only migration. The .NET API stays in nu/ — this component
 * calls existing and new REST endpoints on the nuadvisory API cross-origin.
 *
 * Three tabs:
 *   1. Select Creds — filter dropdowns + paginated search results + checkbox selection
 *   2. Design — template/label dropdowns + drag-to-reorder selected list + Create button
 *   3. History — table of user's past slide creds
 *
 * Auth: uses the nu_assist_session JWT cookie (SameSite=Lax, Secure) which
 * the nuadvisory NuAssistOrCookie policy accepts cross-origin. All fetch
 * calls use `credentials: "include"` — no explicit Authorization header needed.
 *
 * API base URL: VITE_NUADVISORY_API_URL env var.
 */
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  type ChangeEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { useMe } from "@nu-assist/shared-fe";
import { useAdvisoryFetch, type FetchLike } from "../../context/AdvisoryClientContext";
import { NUADVISORY_BASE } from "../../lib/advisory";
import { Button } from "@nu-assist/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CatalogEntry {
  key: string;
  text: string;
}

interface ClosedSearch {
  thriveId: number;
  clientId: number;
  clientName: string;
  jobTitle: string;
  industries?: string[];
  sectors?: string[];
  partners?: string[];
  estimatedCloseYear?: string;
  keywords?: string;
  revenue?: string;
  geography?: string;
  hasLogo?: boolean;
  logoContent?: string;
}

interface ClosedSearchResult {
  searches: ClosedSearch[];
  resultsQty: number;
  pageIndex: number;
  totalPages: number;
}

interface SelectedSearch {
  clientId: number;
  clientName: string;
  searchName?: string;
  labelName?: string;
  jobTitle?: string;
  thriveId: number;
  industries?: string[];
  sectors?: string[];
  partners?: string[];
  estimatedCloseYear?: string;
  keywords?: string;
  revenue?: string;
  geography?: string;
  hasLogo?: boolean;
  logoContent?: string;
}

interface SlideRecord {
  id: string;
  title: string;
  slideUrl?: string;
  createdOn: string;
}

interface SlideGenerateResponse {
  slideUrl: string;
  // When set, the request succeeded on the Google side but a
  // post-processing step (AttachClients / UpdateAsync) failed. The
  // client still receives a usable slideUrl; the consumer (mutation
  // onSuccess) decides how to surface the partial outcome to the user.
  partial?: boolean;
}

interface FilterState {
  searchCriteria: string;
  keywords: string;
  jobFunction: string[];
  positionLevel: string[];
  industry: string[];
  industryAnd: boolean;
  sector: string[];
  sectorAnd: boolean;
  investor: string[];
  investorAnd: boolean;
  investorsOwnersCategory: string[];
  annualRevenueAtTimeOfSearch: string[];
  geography: string[];
  partner: string[];
  estimatedCloseYear: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

const TEMPLATE_STYLES = [
  { value: "Nine_v1", label: "Nine (3x3)" },
  { value: "Nine_v2", label: "Nine (wider)" },
  { value: "Twelve", label: "Twelve" },
  { value: "Fifteen", label: "Fifteen" },
  { value: "Twenty", label: "Twenty" },
] as const;

const LABEL_CONTENTS = [
  { value: "CompanyPosition", label: "Company + Position" },
  { value: "Company", label: "Company" },
  { value: "Position", label: "Position" },
  { value: "None", label: "None" },
] as const;

type TabId = "select" | "design" | "history";

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export function buildFiltersParam(filters: FilterState): string {
  const parts: string[] = [];
  const append = (id: string, values: string[], andMode?: boolean) => {
    if (!values.length) return;
    const sep = andMode ? "|AND|" : "|OR|";
    parts.push(`${id}${sep}${values.join("~")}`);
  };
  append("JobFunction", filters.jobFunction);
  append("PositionLevel", filters.positionLevel);
  append("Industry", filters.industry, filters.industryAnd);
  append("Sector", filters.sector, filters.sectorAnd);
  append("Investor", filters.investor, filters.investorAnd);
  append("InvestorsOwnersCategory", filters.investorsOwnersCategory);
  append("AnnualRevenueAtTimeOfSearch", filters.annualRevenueAtTimeOfSearch);
  append("Geography", filters.geography);
  append("Partner", filters.partner);
  append("EstimatedCloseYear", filters.estimatedCloseYear);
  return parts.join("||");
}

async function fetchCatalog(fetchImpl: FetchLike = fetch): Promise<CatalogEntry[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Searches/catalog?key=`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`catalog ${res.status}`);
  return res.json() as Promise<CatalogEntry[]>;
}

async function fetchClosedSearches(
  filters: FilterState,
  pageNumber: number,
  fetchImpl: FetchLike = fetch,
): Promise<ClosedSearchResult> {
  const params = new URLSearchParams({
    count: String(PAGE_SIZE),
    pageNumber: String(pageNumber),
    searchCriteria: filters.searchCriteria,
    keywords: filters.keywords,
  });
  const filtersParam = buildFiltersParam(filters);
  if (filtersParam) params.set("filters", filtersParam);
  const res = await fetchImpl(
    `${NUADVISORY_BASE}/api/searches/closed?${params.toString()}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`searches/closed ${res.status}`);
  return res.json() as Promise<ClosedSearchResult>;
}

async function createSlide(
  title: string,
  searches: SelectedSearch[],
  fetchImpl: FetchLike = fetch,
): Promise<SlideRecord> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/slides/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, searches }),
  });
  if (!res.ok) throw new Error(`POST /api/slides/ ${res.status}`);
  return res.json() as Promise<SlideRecord>;
}

async function generateSlide(
  slideId: string,
  templateStyle: string,
  templateLabelContent: string,
  searches: SelectedSearch[],
  fetchImpl: FetchLike = fetch,
): Promise<SlideGenerateResponse> {
  const res = await fetchImpl(
    `${NUADVISORY_BASE}/api/slides/${slideId}/generate`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ templateStyle, templateLabelContent, searches }),
    },
  );
  // 207: slide created on Google side but post-processing (AttachClients/UpdateAsync) failed.
  // Treat as success so the user gets the slideUrl rather than a generic error.
  // Keep this fetch helper side-effect-free: signal "partial" via the return
  // value, and let generateSlideMutation.onSuccess decide which toast to fire
  // (consistent with the other helpers in this file).
  if (res.status === 207) {
    const partial = await res.json() as { slideUrl: string; warning: string };
    return { slideUrl: partial.slideUrl, partial: true };
  }
  if (!res.ok) throw new Error(`POST /api/slides/${slideId}/generate ${res.status}`);
  return res.json() as Promise<SlideGenerateResponse>;
}

async function fetchSlideHistory(fetchImpl: FetchLike = fetch): Promise<SlideRecord[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/slides/`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/slides/ ${res.status}`);
  return res.json() as Promise<SlideRecord[]>;
}

interface SlideViewModelResponse {
  id: string;
  title: string;
  createdOn: string;
  settings: string;
  searches: Array<{
    thriveId: number;
    clientId: number;
    clientName: string;
    jobTitle: string;
  }>;
}

async function fetchSlideById(id: string, fetchImpl: FetchLike = fetch): Promise<SlideViewModelResponse> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/slides/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/slides/${id} ${res.status}`);
  return res.json() as Promise<SlideViewModelResponse>;
}

async function uploadLogo(clientId: number, name: string, file: File, fetchImpl: FetchLike = fetch): Promise<string> {
  const form = new FormData();
  form.append("formFile", file);
  const res = await fetchImpl(
    `${NUADVISORY_BASE}/api/clients/logo/${clientId}?name=${encodeURIComponent(name)}`,
    { method: "POST", credentials: "include", body: form },
  );
  if (!res.ok) throw new Error(`POST /api/clients/logo/${clientId} ${res.status}`);
  return res.text();
}

// ---------------------------------------------------------------------------
// Constants — module-scope so they can be shared between useState init
// and the clear-filters handler without duplicating the shape.
// ---------------------------------------------------------------------------

const DEFAULT_FILTERS: FilterState = {
  searchCriteria: "",
  keywords: "",
  jobFunction: [],
  positionLevel: [],
  industry: [],
  industryAnd: false,
  sector: [],
  sectorAnd: false,
  investor: [],
  investorAnd: false,
  investorsOwnersCategory: [],
  annualRevenueAtTimeOfSearch: [],
  geography: [],
  partner: [],
  estimatedCloseYear: [],
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface MultiSelectProps {
  id: string;
  label: string;
  options: string[];
  value: string[];
  onChange: (val: string[]) => void;
  andSwitch?: { value: boolean; onChange: (v: boolean) => void };
  searchable?: boolean;
}

function MultiSelect({ id, label, options, value, onChange, andSwitch, searchable = true }: MultiSelectProps) {
  const [search, setSearch] = useState("");
  const toggle = (opt: string) =>
    onChange(
      value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt],
    );

  const visibleOptions = searchable && search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-label font-medium text-text">
          {label}:
        </label>
        {andSwitch && (
          <span className="text-caption text-text-secondary flex items-center gap-1">
            OR
            <button
              type="button"
              role="switch"
              aria-checked={andSwitch.value ? "true" : "false"}
              aria-label={andSwitch.value ? "Filter mode: AND" : "Filter mode: OR"}
              onClick={() => andSwitch.onChange(!andSwitch.value)}
              className={`relative inline-flex h-4 w-8 rounded-full transition-colors ${andSwitch.value ? "bg-accent" : "bg-border"}`}
            >
              <span
                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${andSwitch.value ? "translate-x-4" : "translate-x-0.5"}`}
              />
            </button>
            AND
          </span>
        )}
      </div>
      {searchable && (
        <input
          type="text"
          placeholder={`Search ${label.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-sm border border-border bg-surface text-body text-text px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label={`Search ${label}`}
        />
      )}
      {/* h-52 = 208px scrollable area (taller list per card #643) — no ui token for fixed list heights */}
      <select
        id={id}
        multiple
        value={value}
        onChange={(e) => {
          // The native <select multiple> only reports VISIBLE selected options.
          // When a search filter is active, preserve selections whose option is
          // currently filtered out of view — otherwise selecting anything while
          // searching would silently drop previously-selected (hidden) values.
          const selectedVisible = Array.from(e.target.selectedOptions).map((o) => o.value);
          const hiddenSelected = value.filter((v) => !visibleOptions.includes(v));
          onChange([...hiddenSelected, ...selectedVisible]);
        }}
        className="rounded-sm border border-border bg-surface text-body text-text h-52 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {visibleOptions.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-0.5 rounded-sm bg-accent-muted px-1.5 py-0.5 text-caption text-accent"
            >
              {v}
              <button
                type="button"
                onClick={() => toggle(v)}
                className="text-accent hover:text-accent-hover leading-none"
                aria-label={`Remove ${v}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface SearchCardProps {
  search: ClosedSearch;
  checked: boolean;
  onToggle: (search: ClosedSearch, checked: boolean) => void;
  onLogoUpload: (search: ClosedSearch, file: File) => void;
}

function SearchCard({ search, checked, onToggle, onLogoUpload }: SearchCardProps) {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Image format is not valid. Please use PNG or JPEG.");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("File size exceeds 1 MB. Please choose a smaller file.");
      return;
    }
    onLogoUpload(search, file);
    e.target.value = "";
  };

  return (
    <li className="flex items-start gap-3 rounded-md border border-border bg-surface p-3 mb-2">
      {/* Checkbox + details live inside a <label>, so clicking anywhere in this
          region toggles the checkbox (not just the small box). The Replace-logo
          button sits OUTSIDE the label, so it never toggles the row. */}
      <label className="flex flex-1 items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(search, e.target.checked)}
          className="mt-1 h-4 w-4 accent-accent"
          aria-label={`Select ${search.clientName} — ${search.jobTitle}`}
        />
        <div className="flex flex-1 gap-3">
          <div className="flex flex-col gap-0.5 w-40 shrink-0">
            <span className="text-body font-semibold text-text">{search.clientName}</span>
            <span className="text-caption text-text-secondary">{search.jobTitle}</span>
            {search.logoContent && (
              <img
                src={search.logoContent}
                alt={`${search.clientName} logo`}
                className="mt-1 h-8 object-contain"
              />
            )}
          </div>
          <ul className="flex-1 text-caption text-text-secondary space-y-0.5">
            {search.industries?.length ? (
              <li>Industries: {search.industries.filter(Boolean).join(", ")}</li>
            ) : null}
            {search.sectors?.length ? (
              <li>Sectors: {search.sectors.filter(Boolean).join(", ")}</li>
            ) : null}
            {search.partners?.length ? (
              <li>Partners: {search.partners.filter(Boolean).join(", ")}</li>
            ) : null}
            {search.estimatedCloseYear ? (
              <li>Est. Close Year: {search.estimatedCloseYear}</li>
            ) : null}
            {search.keywords ? <li>Keywords: {search.keywords}</li> : null}
            {search.geography ? <li>Geography: {search.geography}</li> : null}
            {search.revenue ? <li>Revenue: {search.revenue}</li> : null}
          </ul>
        </div>
      </label>
      <div className="flex flex-col gap-1">
        {!search.hasLogo && (
          <span className="text-caption text-warning">Missing logo</span>
        )}
        <Button
          variant="secondary"
          type="button"
          onClick={() => fileRef.current?.click()}
          className="text-caption py-1"
        >
          {search.hasLogo ? "Replace logo" : "Add Logo"}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          aria-label={`Upload logo for ${search.clientName}`}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </li>
  );
}

interface DraggableSelectedItemProps {
  search: SelectedSearch;
  index: number;
  onRemove: (thriveId: number) => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}

function DraggableSelectedItem({
  search,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  index,
}: DraggableSelectedItemProps) {
  return (
    <li
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2 mb-1 cursor-grab active:cursor-grabbing"
    >
      <span className="text-body text-text-secondary select-none">⠿</span>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-body text-text truncate">{search.clientName}</span>
        {search.jobTitle && (
          <span className="text-caption text-text-secondary truncate">
            {search.jobTitle}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(search.thriveId)}
        className="shrink-0 rounded-sm px-1 text-caption text-error hover:bg-error-muted"
        aria-label={`Remove ${search.clientName}`}
      >
        ✕
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SlideCreator() {
  const me = useMe();
  const queryClient = useQueryClient();
  const advisoryFetch = useAdvisoryFetch();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("select");

  // Select Creds tab state
  const [slideTitle, setSlideTitle] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<number>>(new Set());
  const [selectedSearches, setSelectedSearches] = useState<SelectedSearch[]>([]);
  const [searchResults, setSearchResults] = useState<ClosedSearch[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResultsQty, setTotalResultsQty] = useState(0);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [searchTriggered, setSearchTriggered] = useState(true);

  // Design tab state
  const [currentSlideId, setCurrentSlideId] = useState<string | null>(null);
  const [templateStyle, setTemplateStyle] = useState("Nine_v1");
  const [labelContent, setLabelContent] = useState("CompanyPosition");
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [generatedSlideUrl, setGeneratedSlideUrl] = useState<string | null>(null);

  // Error / status
  const [createError, setCreateError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  const catalogQuery = useQuery({
    queryKey: ["nuadvisory", "catalog"],
    queryFn: () => fetchCatalog(advisoryFetch),
    staleTime: 0,
  });

  // Group catalog entries by key (filter dropdown id)
  const catalogByKey = (catalogQuery.data ?? []).reduce<Record<string, string[]>>(
    (acc, entry) => {
      if (!acc[entry.key]) acc[entry.key] = [];
      if (!acc[entry.key].includes(entry.text)) acc[entry.key].push(entry.text);
      return acc;
    },
    {},
  );

  const closedSearchQuery = useQuery({
    queryKey: ["nuadvisory", "closed-searches", filters, currentPage],
    queryFn: () => fetchClosedSearches(filters, currentPage, advisoryFetch),
    enabled: searchTriggered,
    staleTime: 30 * 1000,
  });

  // Sync search results when query data changes
  useEffect(() => {
    if (closedSearchQuery.data) {
      setSearchResults(closedSearchQuery.data.searches ?? []);
      setTotalResultsQty(closedSearchQuery.data.resultsQty);
      setTotalPages(closedSearchQuery.data.totalPages ?? 1);
    }
  }, [closedSearchQuery.data]);

  const historyQuery = useQuery({
    queryKey: ["nuadvisory", "slide-history"],
    queryFn: () => fetchSlideHistory(advisoryFetch),
    enabled: activeTab === "history",
    staleTime: 30 * 1000,
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const saveSlide = useMutation({
    mutationFn: ({
      title,
      searches,
    }: {
      title: string;
      searches: SelectedSearch[];
    }) => createSlide(title, searches, advisoryFetch),
    onSuccess: (slide) => {
      setCurrentSlideId(slide.id);
      queryClient.invalidateQueries({ queryKey: ["nuadvisory", "slide-history"] });
      setActiveTab("design");
    },
    onError: () => {
      setCreateError("Failed to save slide record. Please try again.");
    },
  });

  const generateSlideMutation = useMutation({
    mutationFn: ({
      slideId,
      style,
      label,
      searches,
    }: {
      slideId: string;
      style: string;
      label: string;
      searches: SelectedSearch[];
    }) => generateSlide(slideId, style, label, searches, advisoryFetch),
    onSuccess: (result) => {
      setGeneratedSlideUrl(result.slideUrl);
      setGenerateError(null);
      toast.success("Slide created successfully! Open it below.");
      // 207 path: backend returned a usable URL but some post-processing
      // failed. Surface a warning so the user knows the slide may be
      // missing logos / labels, but still expose the link.
      if (result.partial) {
        toast.warning(
          "Slide created but some post-processing failed. You can still access it below.",
        );
      }
      queryClient.invalidateQueries({ queryKey: ["nuadvisory", "slide-history"] });
    },
    onError: () => {
      setGenerateError("Google Slides creation failed. Please try again.");
    },
  });

  const logoUploadMutation = useMutation({
    mutationFn: ({
      clientId,
      name,
      file,
    }: {
      clientId: number;
      name: string;
      file: File;
    }) => uploadLogo(clientId, name, file, advisoryFetch),
    onSuccess: (logoContent, variables) => {
      setSearchResults((prev) =>
        prev.map((s) =>
          s.clientId === variables.clientId
            ? { ...s, hasLogo: true, logoContent }
            : s,
        ),
      );
      setSelectedSearches((prev) =>
        prev.map((s) =>
          s.clientId === variables.clientId
            ? { ...s, hasLogo: true, logoContent }
            : s,
        ),
      );
      // Trello #832. The advisory API stores and returns the logo's URL with
      // its upload time as `?v=` (NU-Search/nu#136); until that ships it
      // returns the same bare URL and this refetch changes nothing on screen
      // (it still costs one GET). The cached
      // closed-searches page holds the URL from before the upload, and the
      // effect that copies the list into `searchResults` would bring the old
      // logo back on the next page turn or filter change; refetch it so the
      // list and the card agree.
      queryClient.invalidateQueries({ queryKey: ["nuadvisory", "closed-searches"] });
      toast.success("Logo updated successfully.");
    },
    onError: () => {
      toast.error("Logo upload failed. Please try again.");
    },
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSearch = () => {
    setFilters(pendingFilters);
    setCurrentPage(1);
    setSearchTriggered(true);
  };

  const handleClearFilters = () => {
    setPendingFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const toggleSearchSelection = useCallback(
    (search: ClosedSearch, checked: boolean) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(search.thriveId);
        } else {
          next.delete(search.thriveId);
        }
        return next;
      });
      setSelectedSearches((prev) => {
        if (checked) {
          if (prev.some((s) => s.thriveId === search.thriveId)) return prev;
          return [
            ...prev,
            {
              thriveId: search.thriveId,
              clientId: search.clientId,
              clientName: search.clientName,
              searchName: search.jobTitle,
              jobTitle: search.jobTitle,
              industries: search.industries,
              sectors: search.sectors,
              partners: search.partners,
              estimatedCloseYear: search.estimatedCloseYear,
              keywords: search.keywords,
              revenue: search.revenue,
              geography: search.geography,
              hasLogo: search.hasLogo,
              logoContent: search.logoContent,
            },
          ];
        }
        return prev.filter((s) => s.thriveId !== search.thriveId);
      });
    },
    [],
  );

  const removeFromSelected = useCallback((thriveId: number) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(thriveId);
      return next;
    });
    setSelectedSearches((prev) => prev.filter((s) => s.thriveId !== thriveId));
  }, []);

  const handleSelectAll = () => {
    const pageIds = new Set(searchResults.map((s) => s.thriveId));
    const allPageSelected = searchResults.every((s) => selectedKeys.has(s.thriveId));

    if (allPageSelected) {
      // deselect only the current page — preserve selections from other pages
      setSelectedKeys((prev) => new Set([...prev].filter((id) => !pageIds.has(id))));
      setSelectedSearches((prev) => prev.filter((s) => !pageIds.has(s.thriveId)));
    } else {
      const existingIds = new Set(selectedSearches.map((s) => s.thriveId));
      const toAdd = searchResults
        .filter((s) => !existingIds.has(s.thriveId))
        .map((s) => ({
          thriveId: s.thriveId,
          clientId: s.clientId,
          clientName: s.clientName,
          searchName: s.jobTitle,
          jobTitle: s.jobTitle,
          industries: s.industries,
          sectors: s.sectors,
          partners: s.partners,
          estimatedCloseYear: s.estimatedCloseYear,
          keywords: s.keywords,
          revenue: s.revenue,
          geography: s.geography,
          hasLogo: s.hasLogo,
          logoContent: s.logoContent,
        }));
      setSelectedKeys((prev) => new Set([...prev, ...pageIds]));
      setSelectedSearches((prev) => [...prev, ...toAdd]);
    }
  };

  const computeLabelName = (search: SelectedSearch): string => {
    const name = search.searchName ?? `${search.clientName} - ${search.jobTitle ?? ""}`;
    if (labelContent === "CompanyPosition") return name;
    if (labelContent === "Company") return search.clientName;
    if (labelContent === "Position") return search.jobTitle ?? "";
    return "";
  };

  const handleProceedToDesign = () => {
    if (!slideTitle.trim()) {
      toast.warning("A Title is required to continue with the Slide Cred creation.");
      return;
    }
    if (selectedSearches.length === 0) {
      toast.warning("Select Creds before proceeding to Design.");
      return;
    }
    setCreateError(null);
    // If a slide record already exists for this session (currentSlideId set),
    // skip POST /api/slides/ and just navigate — avoids duplicate history entries.
    // Trade-off: if the user changed the title after the first Next →, the DB
    // record retains the original title. Generation uses searches from the request
    // body (not from DB), so the slide content is always current.
    if (currentSlideId) {
      setActiveTab("design");
      return;
    }
    saveSlide.mutate({ title: slideTitle, searches: selectedSearches });
  };

  const handleCreate = () => {
    if (!currentSlideId) {
      setGenerateError("No slide record found. Please go back and try again.");
      return;
    }
    if (selectedSearches.length === 0) {
      setGenerateError("No searches selected.");
      return;
    }
    const withLabels = selectedSearches.map((s) => ({
      ...s,
      labelName: computeLabelName(s),
    }));
    setGenerateError(null);
    setGeneratedSlideUrl(null);
    generateSlideMutation.mutate({
      slideId: currentSlideId,
      style: templateStyle,
      label: labelContent,
      searches: withLabels,
    });
  };

  const handleLogoUpload = (search: ClosedSearch, file: File) => {
    logoUploadMutation.mutate({
      clientId: search.clientId,
      name: search.clientName,
      file,
    });
  };

  // Drag-to-reorder in Design tab
  const handleDragStart = (index: number) => {
    setDragFromIndex(index);
  };

  const handleDragOver = (index: number) => {
    setDragOverIndex(index);
  };

  const handleDrop = () => {
    if (dragFromIndex === null || dragOverIndex === null) return;
    if (dragFromIndex === dragOverIndex) return;
    setSelectedSearches((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragFromIndex, 1);
      next.splice(dragOverIndex, 0, removed);
      return next;
    });
    setDragFromIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragFromIndex(null);
    setDragOverIndex(null);
  };

  // Reset the selected creds to alphabetical order by company name. Drag-to-reorder
  // lets the user impose a custom order; this is the "go back to A–Z" escape hatch.
  // numeric:true so company names with leading digits sort naturally
  // (e.g. 2U → 10X → 26North → 360training rather than lexicographically).
  const handleSortAlphabetical = () => {
    setSelectedSearches((prev) =>
      [...prev].sort((a, b) =>
        a.clientName.localeCompare(b.clientName, undefined, {
          sensitivity: "base",
          numeric: true,
        }),
      ),
    );
  };

  // Edit a past slide — load its searches into Select Creds tab.
  //
  // Known degradation (deferred follow-up): the server's
  // GET /api/slides/{id} response (SlideViewModelResponse) returns only
  // 4 search-shaped fields (thriveId, clientId, clientName, jobTitle).
  // The other SelectedSearch fields (industries, sectors, partners,
  // keywords, geography, revenue, estimatedCloseYear, hasLogo,
  // logoContent) are not persisted on the server-side slide record and
  // therefore cannot be re-hydrated here. The slide *render* on the
  // backend uses clientId-keyed logo lookups, so logo content is
  // restored at generate time; the metadata fields are display-only on
  // this surface today. If we later add metadata to the rendered slide
  // body, we'll need either (a) to extend the server response, or
  // (b) to re-fetch the closed-search payload here to re-hydrate.
  // Tracked separately from this PR's scope (P2 SlideCreator).
  const editSlideMutation = useMutation({
    mutationFn: (id: string) => fetchSlideById(id, advisoryFetch),
    onSuccess: (data, id) => {
      const loaded: SelectedSearch[] = (data.searches ?? []).map((s) => ({
        thriveId: s.thriveId,
        clientId: s.clientId,
        clientName: s.clientName,
        jobTitle: s.jobTitle,
        searchName: s.jobTitle,
      }));
      setSelectedKeys(new Set(loaded.map((s) => s.thriveId)));
      setSelectedSearches(loaded);
      setCurrentSlideId(id);
      setActiveTab("select");
    },
    onError: () => {
      toast.error("Failed to load slide for editing.");
    },
  });

  // Auth guard — WebShellLayout's AuthGuard wraps this, but defensive narrow
  // so TS knows we're authenticated by render time.
  if (me.status !== "authenticated") return null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-body font-medium border-b-2 transition-colors ${
      activeTab === id
        ? "border-accent text-accent"
        : "border-transparent text-text-secondary hover:text-text hover:border-border"
    }`;

  return (
    <div data-testid="slide-creator" className="p-12">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-h1 text-text mb-6">Create Google Slide Cred</h1>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "select" ? "true" : "false"}
            className={tabClass("select")}
            onClick={() => setActiveTab("select")}
          >
            Select Creds
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "design" ? "true" : "false"}
            className={tabClass("design")}
            onClick={() => handleProceedToDesign()}
          >
            Design
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "history" ? "true" : "false"}
            className={tabClass("history")}
            onClick={() => setActiveTab("history")}
          >
            History
          </button>
        </div>

        {/* Tab: Select Creds */}
        {activeTab === "select" && (
          <div>
            {/* Slide title */}
            <div className="mb-4">
              <label htmlFor="slide-title" className="text-label font-medium text-text block mb-1">
                Slide Cred Title:
              </label>
              <input
                id="slide-title"
                type="text"
                value={slideTitle}
                onChange={(e) => setSlideTitle(e.target.value)}
                placeholder="A title for your new Slide"
                className="w-full max-w-md rounded-md border-2 border-accent bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            {/* Filters */}
            <div className="rounded-md border border-border bg-surface mb-4">
              <div className="p-4 border-b border-border">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label htmlFor="search-criteria" className="text-label font-medium text-text block mb-1">
                      Search criteria:
                    </label>
                    <textarea
                      id="search-criteria"
                      value={pendingFilters.searchCriteria}
                      onChange={(e) =>
                        setPendingFilters((p) => ({
                          ...p,
                          searchCriteria: e.target.value,
                        }))
                      }
                      placeholder="Search for client name or job title"
                      rows={3}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSearch(); } }}
                      className="w-full rounded-sm border border-border bg-surface px-2 py-1 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>

                  <MultiSelect
                    id="JobFunction"
                    label="Job functions"
                    options={catalogByKey.JobFunction ?? []}
                    value={pendingFilters.jobFunction}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, jobFunction: v }))}
                  />

                  <MultiSelect
                    id="PositionLevel"
                    label="Position Level"
                    options={catalogByKey.PositionLevel ?? []}
                    value={pendingFilters.positionLevel}
                    onChange={(v) =>
                      setPendingFilters((p) => ({ ...p, positionLevel: v }))
                    }
                  />

                  <MultiSelect
                    id="Industry"
                    label="Industry"
                    options={catalogByKey.Industry ?? []}
                    value={pendingFilters.industry}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, industry: v }))}
                    andSwitch={{
                      value: pendingFilters.industryAnd,
                      onChange: (v) => setPendingFilters((p) => ({ ...p, industryAnd: v })),
                    }}
                  />

                  <MultiSelect
                    id="Sector"
                    label="Sector"
                    options={catalogByKey.Sector ?? []}
                    value={pendingFilters.sector}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, sector: v }))}
                    andSwitch={{
                      value: pendingFilters.sectorAnd,
                      onChange: (v) => setPendingFilters((p) => ({ ...p, sectorAnd: v })),
                    }}
                  />

                  <MultiSelect
                    id="Investor"
                    label="Investor"
                    options={catalogByKey.Investor ?? []}
                    value={pendingFilters.investor}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, investor: v }))}
                    andSwitch={{
                      value: pendingFilters.investorAnd,
                      onChange: (v) => setPendingFilters((p) => ({ ...p, investorAnd: v })),
                    }}
                  />

                  <MultiSelect
                    id="InvestorsOwnersCategory"
                    label="Investors / Owners Category"
                    options={catalogByKey.InvestorsOwnersCategory ?? []}
                    value={pendingFilters.investorsOwnersCategory}
                    onChange={(v) =>
                      setPendingFilters((p) => ({ ...p, investorsOwnersCategory: v }))
                    }
                  />

                  <MultiSelect
                    id="AnnualRevenueAtTimeOfSearch"
                    label="Annual Revenue At Time Of Search"
                    options={catalogByKey.AnnualRevenueAtTimeOfSearch ?? []}
                    value={pendingFilters.annualRevenueAtTimeOfSearch}
                    onChange={(v) =>
                      setPendingFilters((p) => ({
                        ...p,
                        annualRevenueAtTimeOfSearch: v,
                      }))
                    }
                  />

                  <MultiSelect
                    id="Geography"
                    label="Geography"
                    options={catalogByKey.Geography ?? []}
                    value={pendingFilters.geography}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, geography: v }))}
                  />

                  <MultiSelect
                    id="Partner"
                    label="Partner"
                    options={catalogByKey.Partner ?? []}
                    value={pendingFilters.partner}
                    onChange={(v) => setPendingFilters((p) => ({ ...p, partner: v }))}
                  />

                  <MultiSelect
                    id="EstimatedCloseYear"
                    label="Estimated Close Year"
                    options={catalogByKey.EstimatedCloseYear ?? []}
                    value={pendingFilters.estimatedCloseYear}
                    onChange={(v) =>
                      setPendingFilters((p) => ({ ...p, estimatedCloseYear: v }))
                    }
                  />

                  <div>
                    <label htmlFor="keywords" className="text-label font-medium text-text block mb-1">
                      Keywords:
                    </label>
                    <textarea
                      id="keywords"
                      value={pendingFilters.keywords}
                      onChange={(e) =>
                        setPendingFilters((p) => ({
                          ...p,
                          keywords: e.target.value,
                        }))
                      }
                      placeholder="Type any keyword"
                      rows={3}
                      className="w-full rounded-sm border border-border bg-surface px-2 py-1 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-body text-text-secondary">
                    Results:{" "}
                    <strong>
                      {searchResults.length} / {totalResultsQty}
                    </strong>
                  </span>
                  <div className="flex gap-2">
                    <Button variant="secondary" type="button" onClick={handleSearch}>
                      Search
                    </Button>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={handleClearFilters}
                    >
                      Clear filters
                    </Button>
                  </div>
                </div>
              </div>

              {/* Logo requirements + counter + next */}
              <div className="p-4 flex items-end justify-between gap-4">
                <div className="text-caption text-text-secondary">
                  <p className="font-medium mb-1">Logo requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>PNG or JPEG, max 1 MB</li>
                    <li>Max 3,600 × 800 px</li>
                    <li>Width:height ratio ≈ 1.8:1</li>
                    <li>Minimal margins around logo</li>
                  </ul>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-body font-semibold text-text">
                    Creds Selected: {selectedKeys.size}
                  </span>
                  <Button type="button" onClick={handleProceedToDesign} disabled={saveSlide.isPending}>
                    {saveSlide.isPending ? "Saving…" : "Next →"}
                  </Button>
                </div>
              </div>
            </div>

            {createError && (
              <p className="text-caption text-error mb-3">{createError}</p>
            )}

            {/* Results list */}
            {closedSearchQuery.isLoading && (
              <p className="text-body text-text-muted">Loading searches…</p>
            )}
            {closedSearchQuery.isError && (
              <p className="text-caption text-error">
                Failed to load searches. Please try again.
              </p>
            )}

            {searchResults.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-caption text-accent hover:text-accent-hover mb-2 block"
                >
                  Select All
                </button>
                <ul className="space-y-0">
                  {searchResults.map((s) => (
                    <SearchCard
                      key={s.thriveId}
                      search={s}
                      checked={selectedKeys.has(s.thriveId)}
                      onToggle={toggleSearchSelection}
                      onLogoUpload={handleLogoUpload}
                    />
                  ))}
                </ul>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="secondary"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Prev
                    </Button>
                    <span className="self-center text-body text-text">
                      Page {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab: Design */}
        {activeTab === "design" && (
          <div className="rounded-md border border-border bg-surface p-6">
            <div className="flex flex-col gap-4 max-w-lg mb-6">
              <div className="flex items-center gap-4">
                <label
                  htmlFor="template-style"
                  className="text-label font-medium text-text w-40 shrink-0"
                >
                  Slide Cred Template:
                </label>
                <select
                  id="template-style"
                  value={templateStyle}
                  onChange={(e) => setTemplateStyle(e.target.value)}
                  className="flex-1 rounded-sm border border-border bg-surface px-2 py-1 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {TEMPLATE_STYLES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label
                  htmlFor="label-content"
                  className="text-label font-medium text-text w-40 shrink-0"
                >
                  Slide Label Content:
                </label>
                <select
                  id="label-content"
                  value={labelContent}
                  onChange={(e) => setLabelContent(e.target.value)}
                  className="flex-1 rounded-sm border border-border bg-surface px-2 py-1 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {LABEL_CONTENTS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                type="button"
                onClick={handleCreate}
                disabled={generateSlideMutation.isPending}
                className="self-start"
              >
                {generateSlideMutation.isPending
                  ? "Creating Google Slide…"
                  : "Create"}
              </Button>
            </div>

            {generateError && (
              <p className="text-caption text-error mb-3">{generateError}</p>
            )}

            {generatedSlideUrl && (
              <div className="mb-4 flex items-center gap-3 rounded-md border border-accent bg-accent-muted px-4 py-3">
                <span className="text-body font-medium text-text">Your slide is ready —</span>
                <a
                  href={generatedSlideUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-h3 font-semibold text-accent underline hover:text-accent-hover"
                >
                  Open Slide
                  <ExternalLink size={18} aria-hidden="true" />
                </a>
              </div>
            )}

            <hr className="border-border my-4" />

            <div className="flex items-center justify-between gap-4 mb-2">
              <h2 className="text-h3 text-text">
                Selected Creds ({selectedSearches.length})
              </h2>
              <Button
                type="button"
                variant="secondary"
                onClick={handleSortAlphabetical}
                disabled={selectedSearches.length < 2}
              >
                Sort A–Z
              </Button>
            </div>
            <p className="text-caption text-text-secondary mb-3">
              Drag to reorder, or sort alphabetically by company.
            </p>
            {selectedSearches.length === 0 ? (
              <p className="text-body text-text-muted">No creds selected.</p>
            ) : (
              <ul>
                {selectedSearches.map((s, i) => (
                  <DraggableSelectedItem
                    key={s.thriveId}
                    search={s}
                    index={i}
                    onRemove={removeFromSelected}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tab: History */}
        {activeTab === "history" && (
          <div className="rounded-md border border-border bg-surface">
            {historyQuery.isLoading && (
              <p className="p-4 text-body text-text-muted">Loading history…</p>
            )}
            {historyQuery.isError && (
              <p className="p-4 text-caption text-error">
                Failed to load history. Please try again.
              </p>
            )}
            {!historyQuery.isLoading && !historyQuery.isError && (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-label font-semibold text-text">Title</th>
                    <th className="px-4 py-3 text-label font-semibold text-text">URL</th>
                    <th className="px-4 py-3 text-label font-semibold text-text">Created At</th>
                    <th className="px-4 py-3 text-label font-semibold text-text">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyQuery.data ?? []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-body text-text-muted text-center"
                      >
                        No slides yet.
                      </td>
                    </tr>
                  ) : (
                    (historyQuery.data ?? []).map((slide) => (
                      <tr
                        key={slide.id}
                        className="border-b border-border last:border-0 hover:bg-row-hover"
                      >
                        <td className="px-4 py-3 text-body text-text">{slide.title}</td>
                        <td className="px-4 py-3 text-body">
                          {slide.slideUrl ? (
                            <a
                              href={slide.slideUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent underline truncate max-w-xs block"
                            >
                              {slide.slideUrl}
                            </a>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-body text-text-secondary">
                          {new Date(slide.createdOn).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={() => editSlideMutation.mutate(slide.id)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
